import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { auth, storage } from './firebase';
import { isNativeShell } from './nativeBridge';

/**
 * Hand a file built in the page (PDF, Excel) to the person.
 *
 * In a browser that is the usual download link. Inside the phone apps it is
 * not: neither Android's WebView nor iOS's WKWebView does anything with a
 * download of an in-memory file, so the button looked dead. There the file is
 * put in the user's own storage area and opened by its address, which the app
 * sends to the phone's browser, where it can be viewed, saved or shared.
 */
export async function saveFile(blob: Blob, fileName: string): Promise<void> {
  if (!isNativeShell()) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
    return;
  }

  const uid = auth.currentUser?.uid;
  if (!uid) throw new Error('Sign in again to download this file.');
  const path = `users/${uid}/exports/${crypto.randomUUID()}/${fileName.replace(/[^\w.\- ]+/g, '_')}`;
  const object = ref(storage, path);
  await uploadBytes(object, blob, {
    contentType: blob.type || 'application/octet-stream',
    contentDisposition: `${blob.type === 'application/pdf' ? 'inline' : 'attachment'}; filename="${fileName.replace(/"/g, '')}"`,
  });
  // The app opens any address outside BlisxBench in the phone's browser.
  window.location.href = await getDownloadURL(object);
}

/** For callers that cannot wait: the failure is still shown, never swallowed. */
export function saveFileInBackground(blob: Blob, fileName: string): void {
  saveFile(blob, fileName).catch((e) => {
    window.alert((e as Error)?.message ?? 'Could not download the file.');
  });
}
